import { ITabsVisibility } from '@models/search/ITabsVisibility';
import { IResultTilesSetting } from '@models/search/IResultTilesSetting';
import { IForms } from '@models/Forms/IForms';
import { auth } from '@/auth';
import { SearchService } from './SearchService';

/**
 * Конфигурация поиска (видимость областей, настройки плитки результата, схема
 * фильтров) стабильна в рамках сессии. Раньше её тянули и панель поиска, и страница
 * результатов — на каждое монтирование. Кешируем промис на модуль: параллельные
 * потребители получают один запрос, отказ сбрасывает кеш и допускает повтор.
 */
const cacheOnce = <T>(load: () => Promise<T>, fallback: () => T) => {
    let pending: Promise<T> | undefined;
    const get = () => {
        pending ??= load().catch(() => {
            pending = undefined;
            return fallback();
        });
        return pending;
    };
    get.reset = () => {
        pending = undefined;
    };
    return get;
};

/** Область скрыта, пока конфигурация не пришла: показывать вкладку, которой может не быть, нельзя. */
export const HIDDEN_TABS_VISIBILITY: ITabsVisibility = {
    docsTabHidden: false,
    dictsTabHidden: true,
    reportsTabHidden: true,
    confluenceTabHidden: true,
};

export const DEFAULT_RESULT_TILES_SETTING: IResultTilesSetting = {
    mvpHidden: false,
    bottomNomenclatureOverrideLabel: 'Номенклатура',
    execDepOverrideLabel: '',
    purchDepOverrideLabel: '',
};

export const loadTabsVisibility = cacheOnce(
    () => SearchService.getTabsVisibility().then((res) => res.data),
    () => HIDDEN_TABS_VISIBILITY,
);

export const loadResultTilesSetting = cacheOnce(
    () => SearchService.getResultTilesSetting().then((res) => res.data),
    () => DEFAULT_RESULT_TILES_SETTING,
);

export const loadSearchPanelScheme = cacheOnce<IForms | undefined>(
    () => SearchService.getSearchPanelScheme().then((res) => res.data),
    () => undefined,
);

/** Видимость областей зависит от прав: у другого пользователя она своя. */
export const resetSearchConfigCache = () => {
    loadTabsVisibility.reset();
    loadResultTilesSetting.reset();
    loadSearchPanelScheme.reset();
};

auth.onSessionEnd(resetSearchConfigCache);
